import {
  createGrn,
  createDirectGrn,
  getGrn,
  getGrnItems,
  markReceived,
  applyQc,
  completeQc,
} from '../api/grn';
import {
  toPoGrnPayload,
  toDirectGrnPayload,
  extractGrnScope,
  validateGrnLinesForCreate,
  normalizeGrnStatus,
  toLineQcPayload,
} from './grnEntity';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function mergeScope(...scopes) {
  return scopes.reduce((acc, scope) => ({ ...acc, ...scope }), {});
}

function extractGrnItemsFromResponse(grn) {
  return normalizeList(grn?.items || grn?.grn_items);
}

/** Wizard lines the user actually filled in (mirrors filterGrnLinesForSubmit, mode-agnostic). */
function activeQcLines(lines = []) {
  return lines.filter(l => l.item_id && Math.floor(Number(l.received_quantity) || 0) > 0);
}

/** Match a server GRN item back to the wizard line holding its accept/reject quantities. */
function findLineForGrnItem(item, lines, fallbackIndex) {
  const poItemId = item.purchase_order_item_id || item.po_item_id;
  const match = lines.find((l) => {
    if (l.item_id !== item.item_id) return false;
    const linePoId = l.po_item_id || l.purchase_order_item_id;
    if (poItemId || linePoId) return linePoId === poItemId;
    return true;
  });
  return match || lines[fallbackIndex] || null;
}

async function resolveGrnItems(grn, grnId, scope) {
  const fromResponse = extractGrnItemsFromResponse(grn);
  if (fromResponse.length) return fromResponse;
  return normalizeList(await getGrnItems(grnId, scope));
}

/** Load GRN header + items and normalize workflow status from the server. */
export async function refreshGrnWorkflowState(grnId, scope = {}) {
  const grn = await getGrn(grnId);
  const workflowScope = mergeScope(scope, extractGrnScope(grn));
  const items = normalizeList(await getGrnItems(grnId, workflowScope));
  return {
    grn,
    grnId,
    items,
    status: normalizeGrnStatus(grn.status),
    scope: workflowScope,
  };
}

/**
 * Create a PO-linked GRN atomically (header + items) via POST /inventory/grns/.
 * Backend returns DRAFT; QC and post-to-stock are handled in later wizard steps.
 */
export async function submitPoGrnReceipt({
  grnData,
  purchaseOrderId,
  selectedPo,
  lines,
  scope = {},
  onProgress,
  onGrnCreated,
}) {
  const lineErr = validateGrnLinesForCreate(lines, { isDirect: false });
  if (lineErr) {
    throw new Error(lineErr);
  }

  onProgress?.('Creating GRN…');
  const payload = toPoGrnPayload(grnData, lines, {
    purchaseOrderId,
    selectedPo,
  });
  const grn = await createGrn(payload);
  const grnId = grn.grn_id || grn.id;
  const workflowScope = mergeScope(scope, extractGrnScope(grn));
  onGrnCreated?.({ grn, grnId, scope: workflowScope });

  onProgress?.('Loading GRN items…');
  const grnItems = await resolveGrnItems(grn, grnId, workflowScope);

  return {
    grn,
    grnId,
    grnItems,
    status: normalizeGrnStatus(grn.status) || 'DRAFT',
    scope: workflowScope,
  };
}

/**
 * Create a direct GRN via POST /inventory/grns/direct.
 */
export async function submitDirectGrnReceipt({
  grnData,
  lines,
  scope = {},
  onProgress,
  onGrnCreated,
}) {
  const lineErr = validateGrnLinesForCreate(lines, { isDirect: true });
  if (lineErr) {
    throw new Error(lineErr);
  }

  if (!scope.tenant_id || !scope.hospital_id) {
    throw new Error('Tenant and hospital scope are required for direct GRN — select a receiving store');
  }

  onProgress?.('Creating direct GRN…');
  const grn = await createDirectGrn(toDirectGrnPayload(grnData, lines, scope));
  const grnId = grn.grn_id || grn.id;
  const workflowScope = extractGrnScope(grn);
  onGrnCreated?.({ grn, grnId, scope: workflowScope });

  onProgress?.('Loading GRN items…');
  const grnItems = await resolveGrnItems(grn, grnId, workflowScope);

  return {
    grn,
    grnId,
    grnItems,
    status: normalizeGrnStatus(grn.status) || 'DRAFT',
    scope: workflowScope,
  };
}

/**
 * Mark received (if needed), record per-item QC via POST /inventory/qc/apply-qc/ for every
 * active line (required — items are created with qc_status=PENDING and complete-qc 422s until
 * each one is set), then finalize via POST /inventory/qc/complete-qc/.
 */
export async function applyGrnQualityCheck({
  grnId,
  grnItems = [],
  lines = [],
  scope = {},
  grnData = {},
  grnStatus,
  onProgress,
}) {
  if (!grnId) {
    throw new Error('GRN id is required to submit quality check');
  }

  let status = normalizeGrnStatus(grnStatus);
  let items = grnItems;
  if (!status || !items.length) {
    const refreshed = await refreshGrnWorkflowState(grnId, scope);
    status = refreshed.status;
    items = refreshed.items;
    scope = refreshed.scope;
  }

  if (status === 'DRAFT' || status === 'RECEIVED') {
    onProgress?.('Marking goods as received…');
    const markResult = await markReceived(grnId, { remarks: grnData.remarks }, scope);
    status = normalizeGrnStatus(markResult?.status) || 'QC_PENDING';
  }

  if (status !== 'QC_COMPLETED') {
    const activeItems = items.filter(i => i.is_active !== false);
    const activeLines = activeQcLines(lines);

    for (let i = 0; i < activeItems.length; i += 1) {
      const item = activeItems[i];
      if (item.qc_status && item.qc_status !== 'PENDING') continue;

      const line = findLineForGrnItem(item, activeLines, i);
      if (!line) {
        throw new Error(
          `Missing quality check details for item ${item.item_code || item.item_name || item.item_id}`,
        );
      }

      onProgress?.(`Recording quality check ${i + 1} of ${activeItems.length}…`);
      const itemId = item.grn_item_id || item.id;
      await applyQc(itemId, toLineQcPayload(line), scope);
    }

    const verifyItems = normalizeList(await getGrnItems(grnId, scope));
    const stillPending = verifyItems.filter(
      i => i.is_active !== false && (i.qc_status || 'PENDING') === 'PENDING',
    );
    if (stillPending.length) {
      throw new Error(
        `${stillPending.length} item(s) still have QC pending. Enter accept/reject quantities for all lines and try again.`,
      );
    }

    onProgress?.('Completing quality check…');
    const qcResult = await completeQc(grnId, {}, scope);
    status = normalizeGrnStatus(qcResult?.status) || 'QC_COMPLETED';
  }

  return refreshGrnWorkflowState(grnId, scope);
}
